import React, { useCallback, useEffect, useMemo } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { formatPercentage } from '@chilecompra/react-kit'
import { DataTable, Select, Switch, Toast, useSelect, useSwitch } from '@chilecompra/react-kit/components'
import { SECTOR_DISTRIBUTIONS_DICTIONARY, SECTOR_DISTRIBUTIONS_OPTIONS } from '../../config/settings/constants'
import { REACT_APP_SEARCH_BIDDINGS_URL } from '../../config/settings/environment'
import routes from '../../config/settings/routes'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetSectorBiddingsThunk, onGetSectorBiddingsKPIsThunk } from './SectorBiddingsSection.actions'
import SankeyChart from '../../components/Charts/SankeyChart.v2'
import RegionalPurchasingMechanisms from '../../components/Infographics/RegionalPurchasingMechanisms'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Card,
  Description,
  GridContainer,
  GridItem,
  Info,
  Italic,
  LinkAnchor,
  LinkSpan,
  List,
  ListItem,
  Root,
  Strong,
  Title
} from './SectorBiddingsSection.styles'

/**
 * The SectorBiddingsSection's container.
 */
const SectorBiddingsSection = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { endYear, latestUpdate, startYear } = useSelector(state => state.topNavbar)
  const { id, name } = useSelector(state => state.sectorSection)
  const {
    csv,
    data,
    deserted,
    lessToTwo,
    loading,
    moreThanThree,
    totalAmount,
    totalAmountUSD,
    totalBiddings,
    totalOrders,
    totalPercentage
  } = useSelector(state => state.sectorBiddingsSection)
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      batch(() => {
        dispatch(onGetSectorBiddingsThunk({ id, year: updatedValue }))
        dispatch(onGetSectorBiddingsKPIsThunk({ id, year: updatedValue }))
      }),
    overwriteCallback: () => endYear
  })
  const { value: distributionValue, onChange: handleChangeDistribution } = useSelect({
    initialValue: SECTOR_DISTRIBUTIONS_DICTIONARY.amount
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, distributionValue, yearValue],
    captureImageCallback: () => exportToImage('sector-biddings-sankey-chart', `Info_Sector_Licitaciones_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Sector_Licitaciones_${yearValue}`)
  })
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const enhancedData = useMemo(
    () =>
      data.map(datum => {
        switch (distributionValue) {
          case SECTOR_DISTRIBUTIONS_DICTIONARY.amount: {
            const totalAmountValue = currencyValue ? totalAmountUSD : totalAmount
            const amountValue = currencyValue ? datum.amountUSD : datum.amount

            return {
              from: `Monto total de licitaciones: $${formatAmount(totalAmountValue)}`,
              to: `${datum.name}: $${formatAmount(amountValue)}`,
              weight: totalAmountValue > 0 ? (amountValue * data.length) / totalAmountValue : 0
            }
          }
          case SECTOR_DISTRIBUTIONS_DICTIONARY.biddings: {
            return {
              from: `Total de licitaciones: ${totalBiddings}`,
              to: `${datum.name}: ${datum.biddings}`,
              weight: totalBiddings > 0 ? (datum.biddings * data.length) / totalBiddings : 0
            }
          }
          case SECTOR_DISTRIBUTIONS_DICTIONARY.orders: {
            return {
              from: `Total de ordenes de compra: ${totalOrders}`,
              to: `${datum.name}: ${datum.orders}`,
              weight: totalOrders > 0 ? (datum.orders * data.length) / totalOrders : 0
            }
          }
          case SECTOR_DISTRIBUTIONS_DICTIONARY.percentage: {
            return {
              from: `Porcentaje de licitaciones: ${formatPercentage(totalPercentage)}`,
              to: `${datum.name}: ${formatPercentage(datum.percentage)}`,
              weight: totalPercentage > 0 ? (datum.amount * data.length) / totalPercentage : 0
            }
          }
          default: {
            return { from: null, to: null, weight: null }
          }
        }
      }),
    [currencyValue, data, totalAmount, totalBiddings, totalOrders, distributionValue]
  )
  const handleRedirectToDownloads = useCallback(
    () =>
      navigate(`${routes.downloadOrderAndBiddings}#DescargaReportes`, {
        state: { name, idEntity: id }
      }),
    []
  )

  useEffect(() => {
    if (!endYear || !id) {
      return
    }

    batch(() => {
      dispatch(onGetSectorBiddingsThunk({ id, year: endYear }))
      dispatch(onGetSectorBiddingsKPIsThunk({ id, year: endYear }))
    })
  }, [endYear, id])

  return (
    <Root>
      <GridContainer padding="25px 0 23px">
        <GridItem xs={12}>
          <Title>Licitaciones</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Conoce cifras generales asociadas a todas las licitaciones pertenecientes al sector {name} durante el año
            seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage={disableCaptureImage}
            disableDownloadFile={disableDownloadFile}
            header={
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={2} xs={12}>
                  <Select
                    label="AÑO"
                    loading={loading}
                    options={yearsOptions}
                    value={yearValue}
                    onChange={handleChangeYear}
                  />
                </GridItem>
                <GridItem md={3} xs={12}>
                  <Select
                    label="DISTRIBUCIÓN"
                    loading={loading}
                    options={SECTOR_DISTRIBUTIONS_OPTIONS}
                    value={distributionValue}
                    onChange={handleChangeDistribution}
                  />
                </GridItem>
                <GridItem align="right" md={7} xs={12}>
                  <GridContainer justifyContent="flex-end">
                    <GridItem md={3} xs={12}>
                      <Switch
                        checked={currencyValue}
                        color="primary"
                        labelLeft="CLP"
                        labelRight="USD"
                        margin="0 0 0 16px"
                        onChange={handleChangeCurrency}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
              </GridContainer>
            }
            infographic={<RegionalPurchasingMechanisms />}
            noDataText="El organismo comprador no cuenta con órdenes de compra que requieran una ficha de contrato en el año seleccionado."
            ref={dataLayoutRef}
            updatedDate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onChangeView={handleChangeView}
            onDownloadFile={handleDownloadFile}
          >
            <DataLayoutChild view={DataLayoutViews.CHART}>
              <GridContainer>
                <GridItem xs={12}>
                  <SankeyChart data={enhancedData} id="sector-biddings-sankey-chart" />
                </GridItem>
              </GridContainer>
              <GridContainer margin="32px 0 0" spacing={2}>
                <GridItem md={4} xs={12}>
                  <Card enableHover color="blank" elevation="1" rows={1}>
                    <Strong>{moreThanThree}</Strong> Licitaciones con 3 o más ofertas
                  </Card>
                </GridItem>
                <GridItem md={4} xs={12}>
                  <Card enableHover color="blank" elevation="1" rows={1}>
                    <Strong>{lessToTwo}</Strong> Licitaciones con 2 o menos ofertas
                  </Card>
                </GridItem>
                <GridItem md={4} xs={12}>
                  <Card enableHover color="blank" elevation="1" rows={1}>
                    <Strong>{deserted}</Strong> Licitaciones declaradas desiertas
                  </Card>
                </GridItem>
              </GridContainer>
              <GridContainer margin="32px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Año', value: 0 },
                      { label: 'Nombre', value: 1 },
                      { label: 'Órdenes de compra', value: 2 },
                      { label: 'Licitaciones adjudicadas', value: 3 },
                      { label: 'Monto transado', value: 4 },
                      { label: 'Monto transado (USD)', value: 5 },
                      { label: 'Porcentaje', value: 6 }
                    ]}
                    headersCellsProps={[
                      { width: '5%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, initialSort: true },
                      { enableSort: true },
                      { enableSort: true, width: '5%' }
                    ]}
                    initialRows={6}
                    loading={loading}
                    records={csv.slice(1)}
                    recordsDecorators={[
                      ([year]) => year,
                      ([, name]) => name,
                      ([, , orders]) => orders,
                      ([, , , biddings]) => biddings,
                      ([, , , , amount]) => `$${formatAmount(amount)}`,
                      ([, , , , , amountUSD]) => `$${formatAmount(amountUSD)}`,
                      ([, , , , , , percentage]) => formatPercentage(percentage)
                    ]}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="30px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
      <GridContainer margin="35px 0 0">
        <GridItem align="center" xs={12}>
          <Toast disableIcon>
            <List>
              <ListItem>
                Si deseas descargar las licitaciones del sector {name} puedes ir a la{' '}
                <LinkSpan onClick={handleRedirectToDownloads}>página de descargas</LinkSpan> Datos Abiertos.
              </ListItem>
              <ListItem>
                Para explorar el listado completo de las licitaciones puedes hacerlo en el{' '}
                <LinkAnchor href={REACT_APP_SEARCH_BIDDINGS_URL} rel="noreferrer" target="_blank">
                  buscador de licitaciones
                </LinkAnchor>{' '}
                de Mercado Público.
              </ListItem>
            </List>
          </Toast>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default SectorBiddingsSection
